import { Component, h, State, Host, Prop, Element } from '@stencil/core';
import { translations } from './translations';
import { sv, awaitTopbar } from '@/index';
import type { PnLanguages } from '@/index';

@Component({
  tag: 'pn-ocr-search',
  styleUrl: 'pn-ocr-search.scss',
})
export class PnOcrSearch {
  @Element() hostElement: HTMLElement;

  @State() loading: boolean = false;
  @State() error: boolean = false;
  @State() ocrNumber?: string;
  @State() searchSuccess: boolean = false;
  @State() invalidOcrNumber: boolean = false;

  /** The `language` property will be prioritized before the pnTopbar language. */
  @Prop() language?: PnLanguages = null;

  async componentWillLoad() {
    if (this.language === null) return await awaitTopbar(this.hostElement);
  }

  getTranslation(key: string): string {
    return translations[key][this.language || sv];
  }

  handleSearch(e: CustomEvent<string>) {
    // reset states
    this.invalidOcrNumber = false;
    this.searchSuccess = false;
    this.error = false;

    const validationResult = this.validateOcrNumber(e.detail);

    if (validationResult.error) {
      this.invalidOcrNumber = true;
      return;
    }

    this.ocrNumberSearch(validationResult.value);
  }

  async ocrNumberSearch(ocrNumber: string) {
    this.loading = true;

    await fetch(`https://portal.postnord.com/public-service/api/ocr?key=${ocrNumber}`)
      .then(response => response.json())
      .then(data => {
        this.ocrNumber = data.newOcr;
        this.searchSuccess = true;
      })
      .catch(() => {
        this.error = true;
        this.searchSuccess = false;
      })
      .finally(() => {
        this.loading = false;
      });
  }

  validateOcrNumber(value: string) {
    // Removes spaces and dashes
    const formatValue = value ? value.replace(/[ -]/gi, '') : '';

    // Looks for non-digits
    const ocrRegex = new RegExp(/\D+/);

    // Needs to be atleast 6 numbers
    const error = ocrRegex.test(formatValue) || formatValue.length !== 6;

    return { error, value: formatValue };
  }

  renderSearchResult() {
    if (!this.loading && !this.error && this.searchSuccess) {
      return (
        <div class="search-results">
          {this.getTranslation('SEARCH_SUCCESS')}: <b>{this.ocrNumber}</b>
        </div>
      );
    }
  }

  renderErrorMessage() {
    if (this.invalidOcrNumber && !this.loading) {
      return (
        <div class="invalid-text-container">
          <p class="invalid-ocr">{this.getTranslation('ERROR_INVALID_LICENSE_NUMBER')}</p>
        </div>
      );
    }
    if (this.error && !this.loading) {
      return (
        <div class="error-text-container">
          <p class="bold">{this.getTranslation('ERROR_NO_RESULTS')}</p>
          <p class="bold">{this.getTranslation('ERROR_REASON_HEADER')}</p>
          <p>{this.getTranslation('ERROR_REASON_ONE')}</p>
          <p>{this.getTranslation('ERROR_REASON_TWO')}</p>
        </div>
      );
    }
  }

  render() {
    return (
      <Host>
        <pn-search-field
          loading={this.loading}
          label={this.getTranslation('SEARCH_BUTTON')}
          button="icon"
          buttonLabel={this.getTranslation('SEARCH_BUTTON')}
          placeholder={this.getTranslation('PLACEHOLDER_TEXT')}
          onSearch={e => this.handleSearch(e)}
        />
        {this.renderSearchResult()}
        {this.renderErrorMessage()}
      </Host>
    );
  }
}
